import * as yup from 'yup';

import { Button, Input } from '.';
import { Col, Form, Label, Row, Table } from 'reactstrap';
import { GeneralSettings, SurveyType, SurveyWorksheetResult } from '../common/Types';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import React from 'react';
import { programService } from '../services/ProgramService';
import { toast } from 'react-toastify';

interface SurveyWorksheetResultsFormProps {
    settings: GeneralSettings;
    surveyType: SurveyType;
    surveyResults: SurveyWorksheetResult[];
    selectedYear: number;
    onResultsUpdated: () => void;
    onCancel: () => void;
}

const SurveyWorksheetResultsForm = (props: SurveyWorksheetResultsFormProps) => {
    const { settings, surveyType, surveyResults, onResultsUpdated, onCancel, selectedYear } = props;

    const surveyWorksheetResultSchema = yup.object({
        results: yup.array().of(
            yup.object<SurveyWorksheetResult>({
                resultId: yup.number().required(),
                programId: yup.number().required(),
                surveyTypeId: yup.number().required().default(surveyType.surveyTypeId),
                questionId: yup.number().required(),
                graduatingYear: yup.number().required().default(selectedYear),
                number5: yup.number().required().min(0, 'Must be greater than 0'),
                number4: yup.number().notRequired().nullable(),
                number3: yup.number().notRequired().nullable(),
                number2: yup.number().notRequired().nullable(),
                number1: yup.number().notRequired().nullable(),
                numberNa: yup.number().required().min(0, 'Must be greater than 0'),
                numberOmitted: yup.number().required().min(0, 'Must be greater than 0'),
                surveysReturned: yup.number().notRequired().nullable(),
                totalEntered: yup
                    .number()
                    .test(
                        'total-entered-max',
                        'Total responses entered cannot exceed surveys returned',
                        function (value) {
                            const { number5, numberNa, numberOmitted, surveysReturned } = this.parent;
                            const totalEntered = (number5 || 0) + (numberNa || 0) + (numberOmitted || 0);

                            if (surveysReturned && totalEntered > surveysReturned) {
                                return this.createError({
                                    message: `Total responses entered cannot be more than ${surveysReturned}`,
                                });
                            }

                            return true;
                        },
                    ),
            }),
        ),
    });

    const {
        handleSubmit,
        control,
        watch,
        formState: { isSubmitting, errors },
    } = useForm({
        resolver: yupResolver(surveyWorksheetResultSchema as any),
        defaultValues: { results: surveyResults },
    });
    const { fields } = useFieldArray({
        control,
        name: 'results',
    });

    // Generates name of specific exam based on questionLabeType on General Settings
    const surveyTitle = (survey, index: number) => {
        const letters = [
            'A',
            'B',
            'C',
            'D',
            'E',
            'F',
            'G',
            'H',
            'I',
            'J',
            'K',
            'L',
            'M',
            'N',
            'O',
            'P',
            'Q',
            'R',
            'S',
            'T',
            'U',
            'V',
            'W',
            'X',
            'Y',
            'Z',
        ];
        const prefix = survey.surveyType?.prefix;
        const isLetters: boolean = settings.reportingWindow.questionLabelType === 'Letters';

        return isLetters ? <span>{`${prefix}-${letters[index]}`}</span> : <span>{`${prefix}-${index + 1}`}</span>;
    };

    const getSurveyTypeCutScore = (surveyTypeId: number): number => {
        if (surveyTypeId >= 6 && surveyTypeId <= 8) {
            return +(settings?.reportingWindow?.employeeSurveyCutScore || 0);
        }

        return +(settings?.reportingWindow?.graduateSurveyCutScore || 0);
    };

    const getSurveyTypeSatisfactionThreshold = (surveyTypeId: number): number => {
        if (surveyTypeId >= 6 && surveyTypeId <= 8) {
            return settings.reportingWindow.employeeSurveySatisfactionThreshold || 0;
        }

        return settings.reportingWindow.graduateSurveySatisfactionThreshold || 0;
    };

    const getNumberAboveCutScore = (cutScore: number, surveyResult: SurveyWorksheetResult): number => {
        if (cutScore === 0) {
            return (
                (surveyResult.number1 || 0) +
                (surveyResult.number2 || 0) +
                (surveyResult.number3 || 0) +
                (surveyResult.number4 || 0) +
                (surveyResult.number5 || 0)
            );
        } else {
            return surveyResult.number5 || 0;
        }
    };

    const isPositive = (result: any): boolean => {
        const numberAboveCut = getNumberAboveCutScore(getSurveyTypeCutScore(result.surveyTypeId) || 0, result);
        const numberNaOrOmitted = (Number(result.numberNa) || 0) + (Number(result.numberOmitted) || 0);
        const calculationResponses = (Number(result.surveysReturned) || 0) - numberNaOrOmitted;

        if (calculationResponses < 0) {
            return false;
        }
        if (calculationResponses > 0) {
            if (
                (numberAboveCut / calculationResponses) * 100 >=
                getSurveyTypeSatisfactionThreshold(result.surveyTypeId)
            ) {
                return true;
            }
        } else {
            return true;
        }

        return false;
    };

    const onSubmit = (formData): Promise<void> => {
        return new Promise((res) => {
            const toastId = toast.info('Saving survey data...');

            programService
                .saveSurveyWorksheetResult(formData.results)
                .then((response) => {
                    toast.update(toastId, {
                        type: 'success',
                        render: 'Survey result data saved.',
                    });

                    onResultsUpdated();
                })
                .catch(() => {
                    toast.update(toastId, {
                        type: 'error',
                        render: 'Failed to save survey result data.',
                    });
                })
                .finally(() => {
                    res();
                });
        });
    };

    const watchResults = watch('results');

    return (
        <Form noValidate onSubmit={handleSubmit(onSubmit)}>
            <Row>
                <Col>
                    <Table responsive>
                        <thead>
                            <tr>
                                <th></th>
                                <th>{`# Returned`}</th>
                                {getSurveyTypeCutScore(surveyType.surveyTypeId) === 0 && (
                                    <>
                                        <th>5s</th>
                                        <th>4s</th>
                                        <th>3s</th>
                                        <th>2s</th>
                                        <th>1s</th>
                                    </>
                                )}
                                {getSurveyTypeCutScore(surveyType.surveyTypeId) > 0 && (
                                    <th>{`>= ${getSurveyTypeCutScore(surveyType.surveyTypeId)}`}</th>
                                )}
                                <th>{`N/A`}</th>
                                <th>{`Omitted`}</th>
                                <th>{`Pos / Neg`}</th>
                            </tr>
                        </thead>
                        {fields &&
                            fields.map((survey, i) => (
                                <tbody key={i}>
                                    <tr>
                                        <td style={{ whiteSpace: 'nowrap' }}>{surveyTitle(survey, i)}</td>
                                        <td>{survey.surveysReturned}</td>
                                        {getSurveyTypeCutScore(surveyType.surveyTypeId) === 0 && (
                                            <>
                                                <td>
                                                    <Controller
                                                        name={`results[${i}].number5` as any}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={`number`}
                                                                {...field}
                                                                value={field.value ?? ''}
                                                                style={{ width: '100px' }}
                                                            />
                                                        )}
                                                    />
                                                </td>
                                                <td>
                                                    <Controller
                                                        name={`results[${i}].number4` as any}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={`number`}
                                                                {...field}
                                                                value={field.value ?? ''}
                                                                style={{ width: '100px' }}
                                                            />
                                                        )}
                                                    />
                                                </td>
                                                <td>
                                                    <Controller
                                                        name={`results[${i}].number3` as any}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={`number`}
                                                                {...field}
                                                                value={field.value ?? ''}
                                                                style={{ width: '100px' }}
                                                            />
                                                        )}
                                                    />
                                                </td>
                                                <td>
                                                    <Controller
                                                        name={`results[${i}].number2` as any}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={`number`}
                                                                {...field}
                                                                value={field.value ?? ''}
                                                                style={{ width: '100px' }}
                                                            />
                                                        )}
                                                    />
                                                </td>
                                                <td>
                                                    <Controller
                                                        name={`results[${i}].number1` as any}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                type={`number`}
                                                                {...field}
                                                                value={field.value ?? ''}
                                                                style={{ width: '100px' }}
                                                            />
                                                        )}
                                                    />
                                                </td>
                                            </>
                                        )}
                                        {getSurveyTypeCutScore(surveyType.surveyTypeId) > 0 && (
                                            <td>
                                                <Controller
                                                    name={`results[${i}].number5` as any}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type={`number`}
                                                            {...field}
                                                            value={field.value ?? ''}
                                                            style={{ width: '100px' }}
                                                        />
                                                    )}
                                                />
                                            </td>
                                        )}
                                        <td>
                                            <Controller
                                                name={`results[${i}].numberNa` as any}
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type={`number`}
                                                        {...field}
                                                        value={field.value ?? ''}
                                                        style={{ width: '100px' }}
                                                    />
                                                )}
                                            />
                                        </td>
                                        <td>
                                            <Controller
                                                name={`results[${i}].numberOmitted` as any}
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        type={`number`}
                                                        {...field}
                                                        value={field.value ?? ''}
                                                        style={{ width: '100px' }}
                                                    />
                                                )}
                                            />
                                        </td>
                                        <td>
                                            <div>
                                                {isPositive(watchResults[i]) && (
                                                    <i
                                                        style={{ fontSize: '22px' }}
                                                        className={'mdi mdi-checkbox-marked'}
                                                    ></i>
                                                )}
                                                {!isPositive(watchResults[i]) && (
                                                    <i
                                                        style={{ fontSize: '22px' }}
                                                        className={'mdi mdi-close-box text-danger'}
                                                    ></i>
                                                )}
                                            </div>
                                            <Controller
                                                name={`results[${i}].resultId` as any}
                                                control={control}
                                                render={({ field }) => (
                                                    <input type="hidden" {...field} value={field.value ?? ''} />
                                                )}
                                            />
                                            <Controller
                                                name={`results[${i}].programId` as any}
                                                control={control}
                                                render={({ field }) => (
                                                    <input type="hidden" {...field} value={field.value ?? ''} />
                                                )}
                                            />
                                            <Controller
                                                name={`results[${i}].surveyTypeId` as any}
                                                control={control}
                                                render={({ field }) => (
                                                    <input type="hidden" {...field} value={field.value ?? ''} />
                                                )}
                                            />
                                            <Controller
                                                name={`results[${i}].questionId` as any}
                                                control={control}
                                                render={({ field }) => (
                                                    <input type="hidden" {...field} value={field.value ?? ''} />
                                                )}
                                            />
                                            <Controller
                                                name={`results[${i}].graduatingYear` as any}
                                                control={control}
                                                render={({ field }) => (
                                                    <input type="hidden" {...field} value={field.value ?? ''} />
                                                )}
                                            />
                                            <Controller
                                                name={`results[${i}].surveysReturned` as any}
                                                control={control}
                                                render={({ field }) => (
                                                    <input type="hidden" {...field} value={field.value ?? ''} />
                                                )}
                                            />
                                        </td>
                                    </tr>
                                    {errors?.results && errors.results[i]?.totalEntered?.message && (
                                        <tr>
                                            <td colSpan={getSurveyTypeCutScore(surveyType.surveyTypeId) === 0 ? 10 : 6}>
                                                <Label className={'text-danger'}>
                                                    {errors.results[i]?.totalEntered?.message}
                                                </Label>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            ))}
                    </Table>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Button disabled={isSubmitting} type="submit" color={'primary'}>
                        Save
                    </Button>
                    <Button
                        type={'button'}
                        color={'secondary'}
                        className={'ml-2'}
                        onClick={onCancel}
                    >{`Cancel`}</Button>
                </Col>
            </Row>
        </Form>
    );
};

export default SurveyWorksheetResultsForm;
